import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionActions from '@mui/material/AccordionActions'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Divider from '@mui/material/Divider'
import Grid2 from '@mui/material/Grid2'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { equals, pathOr } from 'ramda'
import React from 'react'

import Button from '@/app/component/atom/button'
import { Radio } from '@/app/component/atom/form'
import Conditions from '@/app/component/conditions'
import SubmitButton from '@/app/component/form/submit-button'
import { FilesState, SenderIdOption } from '@/app/module/campaigns/types'
import {
  addConditionText,
  addVoiceConditionText,
  conditionText,
  generateSummaryText,
  getInboundPersonalization,
  transformConditions,
} from '@/app/module/phonenumbers/helpers'
import {
  ChannelType,
  InboundChannelType,
  InboundHook,
  PhoneNumber,
  PhoneNumberGetFile,
  PhoneNumberSaveFile,
} from '@/app/module/phonenumbers/types'
import { Condition, CountryTimezone, PersonalizationType, SelectOption, TimezoneWithCountryCodeType } from '@/app/types'

import HookAction from './hook-action'
import RuleLabel from './rule-label'
import { useUnsavedRule } from './unsaved-rules.context'

const getEditableFields = (hook: InboundHook, type: InboundChannelType) => ({
  label: hook.label,
  actions: hook.actions,
  conditions: {
    unexpected: type !== ChannelType.Voice && pathOr(false, ['conditions', 'unexpected'], hook),
    conditions: transformConditions(pathOr(null, ['conditions', 'conditions'], hook)),
  },
})

const labelMap: Record<string, string> = {
  sms: 'an SMS on this number with the conditions above',
  voice: 'a voice call on this number with the conditions above',
  whatsapp: 'a WhatsApp message on this number with the conditions above',
}

const generateRadioLabels = ({ noCondition }: { noCondition: boolean }): Record<string, SelectOption<string>[]> => ({
  sms: [
    {
      label: `every SMS${!noCondition ? conditionText : ''}`,
      value: 'false',
    },
    {
      label: `every unexpected SMS${!noCondition ? conditionText : ''}`,
      value: 'true',
    },
  ],
  whatsapp: [
    {
      label: `every WhatsApp message${!noCondition ? conditionText : ''}`,
      value: 'false',
    },
    {
      label: `every unexpected WhatsApp message${!noCondition ? conditionText : ''}`,
      value: 'true',
    },
  ],
})

type Props = {
  countriesTimezones: CountryTimezone[]
  countryCode: string
  expanded: number
  files: FilesState
  hook: InboundHook
  index: number
  orgId: number
  personalizationList: PersonalizationType
  phoneNumber: PhoneNumber
  senderIds: SenderIdOption[]
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string
  type: InboundChannelType

  getFile: PhoneNumberGetFile
  onChange?: (hookId: number) => void
  onError: (payload: { message: string }) => void
  onMicAccessError: () => void
  saveFile: PhoneNumberSaveFile
  saveHook: (hook: InboundHook) => Promise<void>
  setDeleteId: (value: number) => void
}

export type HookProps = Props

const Hook: React.FC<Props> = (props) => {
  const [hook, setHook] = React.useState(props.hook)
  const [conditions, setConditions] = React.useState<Condition | null>(() =>
    transformConditions(pathOr(null, ['conditions', 'conditions'], props.hook)),
  )
  const [showConditionsModal, setShowConditionsModal] = React.useState(false)
  const hasNoconditions = !conditions

  const {
    countriesTimezones,
    countryCode,
    expanded,
    files,
    index: i,
    orgId,
    personalizationList,
    phoneNumber,
    senderIds,
    timezone,
    timezones,
    token,
    type,

    getFile,
    onChange,
    onError,
    onMicAccessError,
    saveFile,
    saveHook,
    setDeleteId,
  } = props
  const [loading, setLoading] = React.useState(false)

  const hookId = hook.id
  const isNewRule = i === -1
  const open = isNewRule || expanded === hookId

  const unexpected = type !== ChannelType.Voice && pathOr(false, ['conditions', 'unexpected'], hook)
  const draft = { label: hook.label, actions: hook.actions, conditions: { unexpected, conditions } }
  const isDirty = !equals(draft, getEditableFields(props.hook, type))
  const currentDraft = React.useRef(draft)
  currentDraft.current = draft
  const previousSource = React.useRef(props.hook)

  React.useEffect(() => {
    if (equals(currentDraft.current, getEditableFields(previousSource.current, type))) {
      setHook(props.hook)
      setConditions(getEditableFields(props.hook, type).conditions.conditions)
    }
    previousSource.current = props.hook
  }, [props.hook, type])

  useUnsavedRule(
    `${type}:${isNewRule ? 'new' : hookId}`,
    isNewRule ? `New rule${hook.label ? `: ${hook.label}` : ''}` : `${i + 1}. ${hook.label || 'Rule'}`,
    isDirty,
  )
  const isMessaging = type === ChannelType.Sms || type === ChannelType.WhatsApp
  const conditionPersonalization = getInboundPersonalization(type, personalizationList)

  const handleRuleSave = async () => {
    if (pathOr(0, ['actions', 'length'], hook) > 0) {
      setLoading(true)
      try {
        await saveHook({
          ...hook,
          conditions: {
            unexpected,
            conditions,
          },
        })
      } finally {
        setLoading(false)
      }
    } else {
      onError({
        message: 'Rule not saved. Please add at least one action.',
      })
    }
  }

  return (
    <StyledAccordion key={hookId} expanded={open} onChange={(_, isExpanded) => onChange?.(isExpanded ? hookId : -1)}>
      <StyledAccordionSummary expandIcon={isNewRule ? null : <ExpandMoreIcon />}>
        <div>
          <RuleLabel
            editable={open}
            index={i}
            label={hook.label}
            onChange={(newLabel) => setHook((s) => ({ ...s, label: newLabel }))}
          />
          <SecondaryHeader className="inbound-hook-summary">
            {generateSummaryText(hook.actions.length, unexpected, type, hasNoconditions)}
          </SecondaryHeader>
        </div>
      </StyledAccordionSummary>
      <AccordionDetails>
        <Grid2 container>
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: type === ChannelType.Voice ? '1rem' : undefined,
              }}
            >
              {isMessaging && (
                <div
                  style={{
                    marginBottom: '-0.5rem',
                    marginLeft: '5px',
                    width: '100%',
                    paddingRight: '10px',
                  }}
                >
                  <Typography color="textSecondary" variant="caption">
                    An <strong>unexpected {type === 'sms' ? 'SMS' : 'WhatsApp message'}</strong> is one that is received
                    when there is no question from a campaign waiting for a reply from the contact.
                  </Typography>
                  <Radio
                    name="unexpected"
                    row={true}
                    value={`${unexpected}`}
                    values={generateRadioLabels({ noCondition: hasNoconditions })[type]}
                    onChange={({ value }) => {
                      setHook({
                        ...hook,
                        conditions: {
                          ...hook.conditions,
                          unexpected: value === 'true',
                        },
                      })
                    }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', width: '100%' }}>
                {hasNoconditions && (
                  <Typography
                    color="textSecondary"
                    sx={{ flex: isNewRule ? 1 : 'unset', paddingRight: '10px', textAlign: 'right' }}
                    variant="caption"
                  >
                    {type === ChannelType.Voice ? addVoiceConditionText : addConditionText}
                  </Typography>
                )}
                <Button
                  className="add-edit-conditions-btn"
                  color="primary"
                  size="small"
                  variant="contained"
                  onClick={() => setShowConditionsModal(true)}
                >
                  {hasNoconditions ? 'Add' : 'Edit'} conditions
                </Button>
              </div>
            </div>
            {showConditionsModal && (
              <Conditions
                data={conditions || ['or', ['and', ['', '', '']]]}
                groups={{}}
                item={[]}
                personalizationList={conditionPersonalization}
                segments={{}}
                title="Run the actions"
                onClose={() => {
                  setShowConditionsModal(false)
                }}
                onSaveConditions={(item: { when: Condition }) => {
                  setConditions(pathOr(null, ['when'], item))
                  setShowConditionsModal(false)
                }}
              />
            )}
            <HookAction
              countriesTimezones={countriesTimezones}
              countryCode={countryCode}
              files={files}
              item={{
                actions: hook.actions,
              }}
              orgId={orgId}
              personalizationList={personalizationList}
              phoneNumber={phoneNumber}
              senderIds={senderIds}
              timezone={timezone}
              timezones={timezones}
              title={`Add/ Edit actions to run upon receiving ${labelMap[type]}`}
              token={token}
              type={type}
              changeHandler={(actions) => {
                setHook({
                  ...hook,
                  actions,
                })
              }}
              getFile={getFile}
              onMicAccessError={onMicAccessError}
              saveFile={saveFile}
            />
          </div>
        </Grid2>
      </AccordionDetails>
      <Divider />
      <AccordionActions>
        {!isNewRule && (
          <div
            style={{
              width: '100%',
            }}
          >
            <DeleteButton
              size="small"
              onClick={() => {
                setDeleteId(hook.id)
              }}
            >
              Remove Rule
            </DeleteButton>
          </div>
        )}
        <SubmitButton isDirty={isDirty} isSubmitting={loading} size="medium" type="button" onClick={handleRuleSave}>
          Save
        </SubmitButton>
      </AccordionActions>
    </StyledAccordion>
  )
}

const StyledAccordion = styled(Accordion)({
  '&::before': {
    opacity: 0,
  },
  borderRadius: '2px',
  marginBottom: '15px',
})

const StyledAccordionSummary = styled(AccordionSummary)({
  backgroundColor: 'rgb(245, 245, 245)',
  padding: '6px 24px 6px 24px',
})

const SecondaryHeader = styled(Typography)({
  fontSize: 15,
  flexBasis: '90%',
  color: 'rgba(0, 0, 0, 0.54)',
})

const DeleteButton = styled(Button)({
  color: '#ff5252',
})

export default Hook
